import React, { MutableRefObject, ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { DefaultTheme, css, keyframes, useTheme } from 'styled-components'

import { useFocus } from '@hozana/dom/hooks/useFocus'
import { useSelector } from '@hozana/redux/hooks'
import { useScreen } from '@hozana/screen/useScreen'
import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'

import { RoundButton } from 'elements/button/RoundButton'
import { AbsoluteDiv } from 'elements/layout/AbsoluteDiv'
import { BorderedDiv, TBorderedDivProps } from 'elements/layout/BorderedDiv'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { FixedDiv } from 'elements/layout/FixedDiv'
import { Overlay } from 'elements/layout/Overlay'
import { Shadow } from 'elements/layout/Shadow'
import { BgImage, TBgImageProps } from 'elements/media/BgImage'
import { P } from 'elements/text/P'

import { ICON } from 'config/icons'
import {
  POPIN_CLOSE_BUTTON_ID,
  POPIN_CONTAINER_ID,
  POPIN_ID,
  POPIN_OVERLAY_ID,
  POPIN_TITLE_ID,
} from 'general/constants'
import { selectPopin } from 'general/selectors'
import { MobilePopover, TMobilePopoverProps } from 'general/structure/Popover/MobilePopover'

type TSlidingAnimationProps = { offset: string; slideFrom?: 'top' | 'bottom' | 'left' | 'right' | 'off' }

const slidingAnimation = ({ offset, slideFrom: from = 'bottom' }: TSlidingAnimationProps) => {
  const translate = ['top', 'bottom'].includes(from) ? 'translateY' : 'translateX'
  const side = ['top', 'left'].includes(from) ? '-' : ''
  return keyframes`
  from { transform: ${translate}(${side}${offset}); }
  to { transform: ${translate}(0); }
`
}

const opacityAnimation = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`

type TPopinWrapperProps = {
  noScroll?: boolean
  mobileFullScreen?: boolean
  gradient?: string
} & TBorderedDivProps &
  Pick<TSlidingAnimationProps, 'slideFrom'>

const PopinWrapper = styled(BorderedDiv)<TPopinWrapperProps>`
  max-width: 100%;
  z-index: ${({ theme }) => theme.zIndex.popin};
  box-shadow: 0 5px 30px 0 rgba(0, 0, 0, 0.2);
  outline: none;
  overflow-y: auto;
  overflow-x: hidden;
  ${({ gradient }) =>
    gradient &&
    css`
      background: ${gradient};
    `}
  ${({ slideFrom }) =>
    slideFrom !== 'off' &&
    css`
      animation: 200ms ease-in ${opacityAnimation}, 200ms ease-in-out ${slidingAnimation({ offset: '20px', slideFrom })};
    `}

  ${mediaQuery('xs', 'xs')} {
    ${({ slideFrom }) =>
      slideFrom !== 'off' &&
      css`
        animation: 200ms ease-in ${opacityAnimation},
          200ms ease-in-out ${slidingAnimation({ offset: '150px', slideFrom })};
      `}
    position: absolute !important;
    ${({ mobileFullScreen, noScroll }) =>
      mobileFullScreen &&
      css`
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        ${!noScroll &&
        css`
          padding-bottom: 250px;
        `}
      `}
    width: auto !important;
    ${({ noScroll }) =>
      !noScroll &&
      css`
        overflow-y: scroll;
      `}
  }
`

const PopinHeader = styled(AbsoluteDiv)`
  ${mediaQuery('xs', 'xs')} {
    text-align: left;
    position: fixed !important;
  }
`
const ArrowWrapper = styled(AbsoluteDiv)<{ topOffset: boolean }>`
  ${mediaQuery('xs', 'xs')} {
    position: fixed;
  }
  ${({ topOffset }) =>
    topOffset &&
    css`
      top: 20px;
    `}
`

const StyledOverlay = styled(Overlay)`
  animation: ${opacityAnimation} 200ms ease;
`

type TPopinTitleProps = {
  title?: ReactNode
  color?: keyof DefaultTheme['colors']
}

export const PopinTitle: TFC<TPopinTitleProps> = ({ title, color }) => (
  <Div>
    <P
      align="center"
      p={{ xs: '1em 55px', sm: '20px 55px 10px' }}
      bold
      fontSize={{ xs: '1em', sm: '1em' }}
      color={color}
      id={POPIN_TITLE_ID}
    >
      {title}
    </P>
  </Div>
)

export type TPopinProps = Omit<TPopinWrapperProps, 'title'> &
  Partial<TPopinTitleProps> &
  Pick<TMobilePopoverProps, 'ornament'> & {
    onClose?: VoidFunction
    /**
     * true: fullscreen everywhere
     * 'mobileOnly' (default): fullscreen on mobile only
     * false: fullscreen nowhere
     */
    fullScreen?: boolean | 'mobileOnly'
    height?: string
    image?: TBgImageProps['src']
    returnArrow?: boolean
    darkOverlay?: boolean
    popoverOnMobile?: boolean
    ids?: {
      closeButton?: string
      container?: string
      overlay?: string
      popin?: string
      title?: string
    }
    'data-testid'?: string
  }

/**
 * ### Popin ###
 */
export const Popin = React.forwardRef<HTMLDivElement, TPopinProps>(
  (
    {
      onClose,
      title,
      children,
      w = '700px',
      fullScreen = 'mobileOnly',
      height = 'auto',
      hidden,
      m = { xs: 0, sm: '5px' },
      image,
      p,
      returnArrow,
      darkOverlay,
      popoverOnMobile,
      ornament,
      'data-testid': dataTestId,
      noScroll = false,
      ids,
      ...otherProps
    },
    ref,
  ) => {
    const theme = useTheme()
    const { t } = useTranslation()
    const screen = useScreen()
    const { name } = useSelector(selectPopin)
    const mobileFullScreen = [true, 'mobileOnly'].includes(fullScreen)

    // focus on mount
    const newRef = useRef<HTMLDivElement>(null)
    const popinWrapperRef = (ref as MutableRefObject<HTMLDivElement>) || newRef
    useFocus(popinWrapperRef)

    const arrow = returnArrow || (screen.xs && returnArrow !== false)

    const content = (
      <>
        {title && (
          <PopinHeader bg={image ? 'transparent' : 'white'} align="center" w="100%" top left right zIndex="popinHeader">
            <PopinTitle title={title} />
          </PopinHeader>
        )}
        {image && (
          <BgImage
            src={image}
            bgSize="cover"
            bg="white"
            h={{ xs: '200px', sm: '230px', md: '250px' }}
            bgPosition="center"
          >
            <Shadow inset depth="20px" x="0" y="-50px" spread="-50px" h="100%" opacity="0.3"></Shadow>
          </BgImage>
        )}
        {/* We set the title here too so that we avoid content overflow when it is in fixed mode (mobile) */}
        {title && !image && (
          <Cell w="100%">
            <PopinTitle title={title} color="white" />
          </Cell>
        )}
        <Div h={title ? 'calc(100% - 57px)' : '100%'} p={p}>
          {children}
        </Div>
      </>
    )

    if (popoverOnMobile && (screen.xs || screen.sm)) {
      return (
        <MobilePopover onClickOverlay={onClose} ornament={ornament}>
          {content}
        </MobilePopover>
      )
    }

    return (
      <FixedDiv
        align="middleCenter"
        id={ids?.container || POPIN_CONTAINER_ID}
        zIndex="popin"
        top
        bottom
        left
        right
        display={hidden && 'none'}
      >
        <StyledOverlay
          bg={darkOverlay ? 'fadedBlack50' : undefined}
          id={ids?.overlay || POPIN_OVERLAY_ID}
          onClick={onClose}
        />
        <PopinWrapper
          ref={popinWrapperRef}
          id={ids?.popin || POPIN_ID}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? ids?.title || POPIN_TITLE_ID : undefined}
          aria-label={!title ? t('common:app.popin-label') : undefined}
          name={name}
          data-testid={dataTestId || QA.COMMON.POPIN.POPIN_WRAPPER}
          bg="white"
          w={{ xs: 'auto', sm: fullScreen === true ? '100%' : w }}
          h={{
            xs: mobileFullScreen ? '100vh' : height,
            sm: fullScreen === true ? '100%' : height,
          }}
          maxH="100vh"
          borderRadius={fullScreen === true ? '0' : { xs: '0', sm: '10px' }}
          borderWidth="0"
          m={fullScreen === true ? 0 : m}
          noScroll={noScroll}
          mobileFullScreen={mobileFullScreen}
          {...otherProps}
        >
          {content}
          {onClose && (
            <ArrowWrapper
              top
              left={arrow}
              right={screen.lg && fullScreen === true ? '20px' : !arrow}
              h={theme.sizes.headerItem}
              w={theme.sizes.headerItem}
              zIndex="popinClose"
              align="middleCenter"
              topOffset={screen.lg && fullScreen === true}
            >
              <RoundButton
                data-testid={QA.COMMON.POPIN.POPIN_BACK}
                onClick={onClose}
                icon={arrow ? ICON.ARROW_LEFT : ICON.CLOSE}
                size={screen.lg && fullScreen === true ? '3em' : '2em'}
                color="black30"
                hoverColor="black50"
                label="trans:common:app.close.by-escape"
                ariaLabel="trans:common:popin.close"
                id={ids?.closeButton || POPIN_CLOSE_BUTTON_ID}
              />
            </ArrowWrapper>
          )}
        </PopinWrapper>
      </FixedDiv>
    )
  },
)

Popin.displayName = 'Popin'
